import { configureStore, Reducer } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  createMigrate,
  PersistedState,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import expireReducer from "redux-persist-expire";
import appReducer from "./features/app/appSlice";
import authReducer from "./features/auth/authSlice";
import claimReducer from "./features/claim/claimSlice";
import complianceReducer from "./features/compliance/complianceSlice";
import subscriptionReducer from "./features/subscription/subscriptionSlice";
import interceptReducer from "./features/intercept/interceptSlice";

// Current version for claim slice - increment when schema changes
const CLAIM_PERSIST_VERSION = 2;

// Initial state for claim slice (must match claimSlice.ts)
// Used to reset state when migrating from incompatible versions
const claimInitialState = {
  currentSegment: "general",
  currentLandingTemplate: "classic",
  navHidden: false,
  showReviewInputs: false,
  quizResult: [],
  claims: [],
  currentFlow: "quiz-landing",
  questions: { quizFlow: [], claimSubmissionFlow: [] },
  leadFormStep: "details",
  leadFormData: {
    fullName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
    signature: null,
    lawsuitSubtype: null,
  },
  leads: [],
  submittedQuizResult: [],
  quizCurrentStep: 1,
  quizScreenNumber: 0,
};

// Migration functions for claim slice
// Each key is the version to migrate TO
const claimMigrations = {
  // Migration to version 2: Reset entire state due to schema changes
  // - questions structure changed from array to { quizFlow, claimSubmissionFlow }
  // - leadFormData now includes lawsuitSubtype
  // - Validation logic moved to backend
  2: (state: PersistedState) => {
    if (import.meta.env.DEV) {
      console.log("[redux-persist] Migrating claim state to version 2 - resetting to initial state");
    }
    // Type-only cast: redux-persist's PersistedState requires `_persist`,
    // but on a fresh install the incoming state (and thus `_persist`) can be
    // undefined — createMigrate handles that case at runtime.
    return {
      ...claimInitialState,
      _persist: state?._persist,
    } as PersistedState;
  },
};

// Helper to create persisted reducer with proper migration support
const createPersistedReducer = (
  key: string,
  whitelist: string[],
  reducer: Reducer,
  version: number = 1,
  migrations?: Record<number, (state: PersistedState) => PersistedState>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- redux-persist transforms are heterogeneous
  transforms?: any[]
) =>
  persistReducer(
    {
      key,
      version,
      storage,
      whitelist,
      ...(migrations && {
        migrate: createMigrate(migrations, { debug: process.env.NODE_ENV === "development" }),
      }),
      ...(transforms && transforms.length > 0 && { transforms }),
    },
    reducer
  );

const persistedAuthReducer = createPersistedReducer(
  "auth",
  ["isAuthenticated", "user", "hasExited", "accessToken"],
  authReducer
);

// Claim reducer with migration support for schema changes
// Claims cache expires after 24 hours — stale-while-revalidate pattern refreshes on every page load
const persistedClaimReducer = createPersistedReducer(
  "claim",
  ["currentSegment", "quizResult", "questions", "claims", "leads", "submittedQuizResult", "leadFormData"],
  claimReducer,
  CLAIM_PERSIST_VERSION,
  claimMigrations,
  [
    expireReducer("claim", {
      expireSeconds: 86400, // 24 hours — cached claims shown instantly, background refresh keeps them fresh
      expiredState: {
        sessionId: null,
        quiz: [],
      },
    }),
  ]
);

// Subscription reducer - persisted for session continuity
const persistedSubscriptionReducer = createPersistedReducer(
  "subscription",
  ["isSubscribed", "isPremium", "isTrialing", "plan", "status", "currentPeriodEnd", "cancelAtPeriodEnd", "trialEnd", "lastFetched"],
  subscriptionReducer
);

const store = configureStore({
  reducer: {
    app: appReducer,
    auth: persistedAuthReducer,
    claim: persistedClaimReducer,
    compliance: complianceReducer,
    subscription: persistedSubscriptionReducer,
    // Intercept is deliberately NOT persisted — a stale intercept
    // result firing on a refreshed page could route the user to a
    // retainer for a no-longer-live campaign.
    intercept: interceptReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export default store;
